<template>
  <AppDialog :is-open="isDialogOpened"
             size="xl"
             :persistent="true"
             :ok-text="t('startUpload')"
             :ok-disabled="selectedDocuments.length <= 0"
             data-locator="documents-upload-dialog"
             @ok="startSelectedDocumentsUpload"
             @cancel="closeDialog"
  >
    <template #title>
      <span>{{ dialogTitle }}</span>
    </template>
    <template #body>
      <div
        class="DocumentsFileUpload-dropzone pa-12 d-flex flex-column align-center row-gap-4"
        data-locator="file-upload-drop-zone"
        @dragover.prevent
        @dragenter.prevent="dragInProgress = true"
        @dragleave.prevent="dragInProgress = false"
        @drop.prevent="dropFileUpload"
      >
        <img
          width="145"
          alt="uploaderIllustration"
          src="@/assets/img/icons/uploaderIllustration.svg"
        />
        <span class="text--medium font-weight-bold">
          {{ canUploadFolders ? t("dropZoneText1") : t("dropZoneText3") }}
        </span>

        <v-skeleton-loader v-if="roomProvidersPending" type="text"/>
        <div v-else class="text-center">
          <p v-if="!isDataRoom" v-html="t('dropZoneText2', { maxSize: maxSignableSize, maxPage: maxSignablePages })" />
          <p>{{ t('maxFileSize', { maxSize: maxUploadFileSize }) }}</p>
        </div>
      </div>

      <div class="d-flex justify-center column-gap-4 my-4">
        <app-button
          v-if="canUploadFolders"
          variant="project"
          data-locator="import-folders-button"
          @click="onFolderInputClick"
        >
          <template #left-icon>
            <app-icon-ds
              icon="folder-upload"
              weight="far"
              size="xs"
            />
          </template>
          {{ t("importFoldersBtn") }}
        </app-button>
        <app-button variant="project" data-locator="import-files-button" @click="onFileInputClick">
          <template #left-icon>
            <app-icon-ds
              icon="file-upload"
              weight="far"
              size="xs"
            />
          </template>
          {{ t("importFilesBtn") }}
        </app-button>
      </div>

      <v-list v-if="selectedDocuments.length > 0">
        <template v-for="(document, i) in selectedDocuments" :key="`doc-${i}`">
          <v-list-item class="px-0">
            <template v-slot:prepend>
              <template v-if="document.type === 'file'">
                <ClosdFildersIcon :document="document.fileObject"/>
              </template>
              <template v-if="document.type === 'folder'">
                <ClosdFildersIcon :document="document"/>
              </template>
            </template>

            <template v-if="document.type === 'file'">
              {{ document.fileObject.name }}
            </template>
            <template v-if="document.type === 'folder'">
              {{ document.name }}
            </template>

            <v-list-item-action>
              <app-button type="icon" @click="() => onDocumentDelete(i)">
                <app-icon
                  icon-name="trash"
                  icon-weight="fas"
                  size="small"
                />
              </app-button>
            </v-list-item-action>
          </v-list-item>
          <v-divider
            v-if="i !== selectedDocuments.length - 1"
            :key="`divider-${i}`"
          ></v-divider>
        </template>
      </v-list>

      <input
        ref="fileInput"
        class="d-none"
        type="file"
        :multiple="true"
        @change="buildAddFiles($event.target.files)"
      />
      <input
        ref="folderInput"
        class="d-none"
        type="file"
        :multiple="true"
        :webkitdirectory="true"
        :allowdirs="true"
        :directory="true"
        @change="buildSelectedFolder($event.target.files)"
      />
    </template>
  </AppDialog>
</template>

<script>
import { cloneDeep } from 'lodash-es'
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'

import AppDialog from '@/common/dialogs/AppDialog.vue'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import { BtoMB } from '@/common/utils/sizes'
import { toast } from '@/common/utils/toast'
import { useTranslation } from '@/common/utils/translation'
import { dialogMixin } from '@/mixins/dialogMixin'
import { FileToUpload, FolderToUpload } from '@/models/documentToUpload.model'
import { START_UPLOAD } from '@/store/modules/documents-upload-files/action_types'
import { ADD_FILES, ADD_FOLDER, REMOVE_DOCUMENT } from '@/store/modules/documents-upload-files/mutation_types'

export default {
  name: 'DocumentsFileUploadDialog',
  components: {
    AppDialog,
    ClosdFildersIcon,
  },
  mixins: [dialogMixin],
  setup () {
    return useTranslation({ keyPrefix: 'project.documents.dialogs.DocumentsFileUploadDialog' })
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      dragInProgress: false,
      folderKey: 1,
      providersMaxSizes: [],
      trashIconColor: -1,
    }
  },
  computed: {
    ...mapState('documentsUploadFiles', ['selectedDocuments']),
    ...mapState('documents', ['paginatedDocumentsCurrentFolderId']),
    ...mapState('room', ['currentRoom', 'roomProvidersPending']),
    ...mapGetters('room', ['currentUserRights', 'isDataRoom', 'isCurrentUserPm']),
    ...mapGetters('documentsBreadcrumb', ['hasTodoFolderOrChild']),
    canUploadFolders () {
      return this.currentUserRights.canCreateFolders && !this.hasTodoFolderOrChild
    },
    dialogTitle () {
      return this.canUploadFolders && !this.hasTodoFolderOrChild
        ? this.t('dialogTitlePm')
        : this.t('dialogTitleGuests')
    },
    maxSignableSize () {
      if (!this.isDataRoom) {
        const availableSize = this.providersMaxSizes.length !== 0 ? BtoMB(Math.max(...this.providersMaxSizes)) : this.maxUploadFileSize
        return Number(availableSize).toFixed(1)
      } else {
        // We won't sign, so let's not compute anything, there's no size
        return 0
      }
    },
    maxSignablePages () {
      return import.meta.env.VUE_APP_MAX_SIGNABLE_PAGES
    },
    maxUploadFileSize () {
      return this.isDataRoom
        ? import.meta.env.VUE_APP_MAX_FILE_SIZE_DR
        : import.meta.env.VUE_APP_MAX_FILE_SIZE
    },
  },
  async created () {
    if (!this.isDataRoom && this.isCurrentUserPm) {
      this.providersMaxSizes = this.currentRoom.providers?.map(o => o.maxSize) || []
    }
  },
  methods: {
    ...mapActions('documentsUploadFiles', [START_UPLOAD]),
    ...mapMutations('documentsUploadFiles', [ADD_FILES, ADD_FOLDER, REMOVE_DOCUMENT]),
    onFileInputClick () {
      this.$refs.fileInput.click()
    },
    onFolderInputClick () {
      this.$refs.folderInput.click()
    },
    buildAddFiles (files) {
      const array = []
      for (const file of files) {
        array.push(new FileToUpload(file, 'document', this.paginatedDocumentsCurrentFolderId))
      }
      this.ADD_FILES(array)
      this.$refs.fileInput.value = null
    },
    buildSelectedFolder (files) {
      const folder = new FolderToUpload(files[0].webkitRelativePath.split('/')[0], [], this.paginatedDocumentsCurrentFolderId)
      for (const file of files) {
        const path = file.webkitRelativePath.split('/')
        path.pop()
        if (path.length === 1) {
          folder.children.push(new FileToUpload(file, 'document'))
        } else {
          path.splice(0, 1)
          this.pushInFolder(file, path, folder)
        }
      }
      this.ADD_FOLDER(folder)
    },
    pushInFolder (file, path, parent) {
      const pathExist = parent.children.find(child => child.name === path[0])
      if (pathExist) {
        if (path.length === 1) {
          pathExist.children.push(new FileToUpload(file, 'document'))
        } else {
          const newPath = cloneDeep(path)
          newPath.splice(0, 1)
          this.pushInFolder(file, newPath, pathExist)
        }
      } else if (path.length === 1) {
        parent.children.push(new FolderToUpload(path[0], [new FileToUpload(file, 'document')]))
      } else {
        parent.children.push(new FolderToUpload(path[0], []))
        const newParent = parent.children.find(child => child.name === path[0])
        const newPath = cloneDeep(path)
        newPath.splice(0, 1)
        this.pushInFolder(file, newPath, newParent)
      }
    },
    async dropFileUpload (e) {
      const dataTransferItemList = e.dataTransfer ? e.dataTransfer.items : 'null'
      const fileEntries = []
      const queue = []
      for (const item of dataTransferItemList) {
        queue.push(item.webkitGetAsEntry())
      }
      while (queue.length > 0) {
        const entry = queue.shift()
        if (entry.isFile) {
          fileEntries.push(await this.getFileFromFileEntry(entry, this.paginatedDocumentsCurrentFolderId))
        } else if (entry.isDirectory && this.canUploadFolders) {
          fileEntries.push(await this.getFolderFromFileEntry(entry, this.paginatedDocumentsCurrentFolderId))
        } else if (entry.isDirectory && !this.canUploadFolders) {
          toast.error(this.t('noFolderAsGuest'))
        }
      }
      this.ADD_FILES(fileEntries)
    },
    async getFolderFromFileEntry (fileEntry, parentId) {
      const folder = parentId !== undefined
        ? new FolderToUpload(fileEntry.name, [], parentId)
        : new FolderToUpload(fileEntry.name, [])
      const readEntries = await this.readEntriesPromise(fileEntry.createReader())
      while (readEntries.length > 0) {
        const entry = readEntries.shift()
        if (entry.isFile) {
          folder.children.push(await this.getFileFromFileEntry(entry))
        } else if (entry.isDirectory) {
          folder.children.push(await this.populateFolder(entry))
        }
      }
      return folder
    },
    async readEntriesPromise (directoryReader) {
      try {
        return await new Promise((resolve, reject) => {
          directoryReader.readEntries(resolve, reject)
        })
      } catch (err) {
        console.error(err)
      }
    },
    async getFileFromFileEntry (fileEntry, parentId) {
      try {
        return parentId !== undefined
          ? new FileToUpload(await new Promise((resolve, reject) => fileEntry.file(resolve, reject)), 'document', parentId)
          : new FileToUpload(await new Promise((resolve, reject) => fileEntry.file(resolve, reject)), 'document')
      } catch (err) {
        console.error(err)
      }
    },
    async populateFolder (fileEntry) {
      const folder = new FolderToUpload(fileEntry.name, [])
      const readEntries = await this.readEntriesPromise(fileEntry.createReader())
      while (readEntries.length > 0) {
        const entry = readEntries.shift()
        if (entry.isFile) {
          folder.children.push(await this.getFileFromFileEntry(entry))
        } else if (entry.isDirectory) {
          folder.children.push(await this.populateFolder(entry))
        }
      }
      return folder
    },
    startSelectedDocumentsUpload () {
      this.START_UPLOAD()
    },
    onDocumentDelete (documentIndex) {
      this.REMOVE_DOCUMENT(documentIndex)
    },
  },
}
</script>

<style scoped lang="scss">
.DocumentsFileUpload-dropzone {
  background-color: #efeff1;
  border: 2px dashed #e0e0e0;
  border-radius: 7px;
  z-index: 1;
  top: 5px;
  position: sticky;
}

:deep(.v-list-item__content) {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
